import { useState, useCallback } from "react";
import api from "../api/axios";

/* ─── Tipos alineados con backend ─── */
export type SadTipo = "puntual" | "continua";
export type SadEstado =
  | "borrador"
  | "pendiente"
  | "aceptada"
  | "rechazada"
  | "rechazada_silenciada"
  | "expirada"
  | "cancelada_por_solicitante"
  | "aplicacion_fallida";

export interface SadFecha {
  id?: number;
  sadId?: number;
  fecha: string;
  horaInicio?: string | null;
  horaFin?: string | null;
}
export interface SadFranja {
  id?: number;
  sadId?: number;
  diaSemana: number;
  horaInicio: string;
  horaFin: string;
}

export interface Sad {
  id: number;
  profesorUserId: number;
  creadoPorUserId: number;
  creadoPorRol?: string | null;
  creadoPor?: { id: number; nombreCompleto?: string | null } | null;

  proyectoId: number;
  asignaturaId?: number | null;
  centroId?: number | null;
  formatoId?: number | null;
  idioma?: string | null;

  tipo: SadTipo;
  periodoInicio?: string | null; // YYYY-MM-DD
  periodoFin: string; // YYYY-MM-DD

  sesiones: number;
  horasTotales: string; // DECIMAL → string
  estado: SadEstado;

  expiraEn?: string | null;
  respondidaEn?: string | null;
  aceptadaEn?: string | null;
  aplicadaEn?: string | null;
  aplicacionDestino?: "especial" | "base" | null;
  puntuaGamificacion: boolean;

  // ⬇️ timestamps del backend (faltaban)
  createdAt: string; // ISO
  updatedAt: string; // ISO

  // relaciones opcionales
  fechas?: SadFecha[];
  franjas?: SadFranja[];
}

/* ─── DTOs ─── */
export type CrearSadPayload =
  | {
      // SAD puntual
      tipo: "puntual";
      profesorUserId: number;
      proyectoId: number;
      asignaturaId?: number;
      centroId?: number;
      formatoId?: number;
      idioma?: string;
      periodoFin: string;
      sesiones: number;
      horasTotales: number;
      fechasPuntuales: { fecha: string; horaInicio: string; horaFin: string }[];
    }
  | {
      // SAD continua
      tipo: "continua";
      profesorUserId: number;
      proyectoId: number;
      asignaturaId?: number;
      centroId?: number;
      formatoId?: number;
      idioma?: string;
      periodoInicio: string;
      periodoFin: string;
      sesiones: number;
      horasTotales: number;
      diasSemana: number[]; // 1..7
      franjas: { diaSemana: number; horaInicio: string; horaFin: string }[];
    }
  | {
      // SAD periódico (para cubrir series recurrentes)
      tipo: "periodico";
      profesorUserId: number;
      proyectoId: number;
      asignaturaId?: number;
      centroId?: number;
      formatoId?: number;
      idioma?: string;
      patron: "WEEKLY" | "MONTHLY" | "DAILY5";
      diasSemana?: number[];
      intervalo?: number;
      horaInicio: string;
      horaFin: string;
      periodoInicio: string;
      periodoFin: string;
    };

export interface RejectMutePayload {
  indefinido: boolean;
  hasta?: string; // YYYY-MM-DD si indefinido=false
}

export interface CloneToPayload {
  profesorId: number;
}

/* ─── Listado ─── */
export interface ListSadQuery {
  estado?: SadEstado;
  profesorUserId?: number;
  creadoPorUserId?: number;
  proyectoId?: number;
  desde?: string; // ISO date/datetime
  hasta?: string; // ISO
}

/* ─── Hook principal (staff) ─── */
export function useSad() {
  const [rows, setRows] = useState<Sad[]>([]);
  const [loading, setL] = useState(false);

  const refresh = useCallback((q: ListSadQuery = {}) => {
    setL(true);
    return api
      .get<Sad[]>("/sad", { params: q })
      .then((r) => setRows(r.data))
      .finally(() => setL(false));
  }, []);

  const getById = useCallback((id: number) => api.get<Sad>(`/sad/${id}`).then((r) => r.data), []);
  const create = useCallback(
    (dto: CrearSadPayload) => api.post<Sad>("/sad", dto).then((r) => r.data),
    []
  );
  const send = useCallback(
    (id: number) => api.post<Sad>(`/sad/${id}/send`).then((r) => r.data),
    []
  );
  const cancel = useCallback(
    (id: number) => api.post<Sad>(`/sad/${id}/cancel`).then((r) => r.data),
    []
  );
  const cloneTo = useCallback(
    (id: number, dto: CloneToPayload) =>
      api.post<Sad>(`/sad/${id}/clone-to`, dto).then((r) => r.data),
    []
  );

  return { rows, loading, refresh, getById, create, send, cancel, cloneTo };
}

/* ─── Hook de acciones del profesor ─── */
export function useSadProfessorActions() {
  const accept = (id: number) => api.post<Sad>(`/sad/${id}/accept`).then((r) => r.data);
  const reject = (id: number) => api.post<Sad>(`/sad/${id}/reject`).then((r) => r.data);
  const rejectMute = (id: number, dto: RejectMutePayload) =>
    api.post<Sad>(`/sad/${id}/reject-mute`, dto).then((r) => r.data);

  const getMyFirstPending = () =>
    api.get<Sad | null>(`/sad/pending/mine/first`).then((r) => r.data);

  return { accept, reject, rejectMute, getMyFirstPending };
}
